"use client";

import React, { useState } from "react";
import { FunnelIcon } from "@heroicons/react/24/outline";
import { EyeIcon, EyeSlashIcon } from "@heroicons/react/24/outline";
import { Button } from "@/components/ui/button";
import { ChipFilterGroup } from "@/components/ChipFilterGroup/ChipFilterGroup";
import { DashboardFiltersFormData } from "@/modules/stochastic/features/Reports/SCDashboard/hooks/dashboardFiltersFormSchema";
import { useStochasticDashboardFilters } from "@/modules/stochastic/features/Reports/SCDashboard/hooks/useStochasticDashboardFilters";
import { DashboardTabs } from "@/modules/stochastic/features/Reports/SCDashboard/components/DashboardTabs/DashboardTabs";
import { UseFormReturn } from "react-hook-form";

type TabType = "sales" | "customers" | "review";

interface StochasticDashboardFiltersProps {
  chartFiltersForm: UseFormReturn<DashboardFiltersFormData>;
  onApplyFilters: () => void;
  areFiltersDirty: boolean;
  activeTab: TabType;
  setActiveTab: (tab: TabType) => void;
}

export const StochasticDashboardFilters: React.FC<
  StochasticDashboardFiltersProps
> = ({
  chartFiltersForm,
  onApplyFilters,
  areFiltersDirty,
  activeTab,
  setActiveTab,
}) => {
  const [showActiveFilters, setShowActiveFilters] = useState(true);
  const {
    filterConfigs,
    filterValues,
    handleFilterChange,
    handleFilterClearAll,
  } = useStochasticDashboardFilters(chartFiltersForm);

  return (
    <div className="mb-2 space-y-2 sticky top-12 z-10 bg-gray-50 dark:bg-slate-950 pb-2">
      <div className="rounded-lg border border-gray-200 dark:border-slate-700 bg-white dark:bg-slate-800 text-card-foreground shadow-sm px-4 py-3">
        <div className="flex flex-col sm:flex-row sm:items-center gap-2">
          <div className="flex items-center gap-1 text-gray-600 dark:text-gray-400">
            <FunnelIcon className="h-4 w-4" />
            <span className="text-sm font-medium">Filters</span>
          </div>

          <ChipFilterGroup
            filters={filterConfigs}
            onChange={handleFilterChange}
            onClearAll={handleFilterClearAll}
            showActiveFilters={false}
            showChips={true}
            values={filterValues}
          />

          <div className="flex items-center gap-2 w-full sm:w-auto sm:ml-auto">
            {areFiltersDirty && (
              <span className="hidden sm:inline text-yellow-600 text-sm">
                Filters changed
              </span>
            )}
            <Button
              className="flex-1 sm:flex-none dark:bg-gray-800 dark:border-white/20 dark:text-white dark:hover:bg-gray-700"
              onClick={() => setShowActiveFilters(!showActiveFilters)}
              size="default"
              type="button"
              variant="outline"
            >
              {showActiveFilters ? (
                <EyeSlashIcon className="h-3.5 w-3.5" />
              ) : (
                <EyeIcon className="h-3.5 w-3.5" />
              )}
              {showActiveFilters ? "Hide Filters" : "Show Filters"}
            </Button>
            <Button
              className="bg-primary hover:bg-primary/90 flex-1 sm:flex-none"
              onClick={onApplyFilters}
              size="default"
              type="button"
            >
              Apply Filters
            </Button>
          </div>
        </div>

        {showActiveFilters && (
          <ChipFilterGroup
            filters={filterConfigs}
            onChange={handleFilterChange}
            onClearAll={handleFilterClearAll}
            showActiveFilters={true}
            showChips={false}
            values={filterValues}
          />
        )}
      </div>

      <div className="rounded-lg border border-gray-200 dark:border-slate-700 bg-white dark:bg-slate-800 shadow-sm p-1">
        <DashboardTabs activeTab={activeTab} onTabChange={setActiveTab} />
      </div>
    </div>
  );
};
